/**
 * EmployeeGb74AnswerPage — 社員Web版（QRログイン等の通常セッション）用の GB人財(74問) 回答フロー
 *
 * モバイルの回答画面（EmbeddedQuestionnairePage）と同一の縦(portrait)フローを、
 * ネイティブ token 注入ではなく通常の Cognito セッション（ProtectedRoute）で描画する。
 * 横向きゲート（VerticalGuide）は出さない（enableOrientationGuide=false / preferredOrientation=portrait）。
 * 完了時はモバイルの notifyAnswerDone ではなくダッシュボードへ戻す。
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IntegratedQuestionnaireFlow } from '../questionnaire/components/IntegratedQuestionnaireFlow';
import {
  fetchQuestionSets,
  fetchQuestionSetById,
  startSession,
  submitAnswersBulk,
  submitMultiFormatAnswersBulk,
  completeSession,
} from '../questionnaire/api/questionnaireApi';
import type { QuestionSet, UserAnswer, SubmitAnswerResponse } from '../questionnaire/types';

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      color: '#6b7280',
      fontFamily: 'system-ui, sans-serif',
      textAlign: 'center',
      padding: '1.5rem',
    }}
  >
    {children}
  </div>
);

export const EmployeeGb74AnswerPage: React.FC = () => {
  const navigate = useNavigate();
  const [questionSet, setQuestionSet] = useState<QuestionSet | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // 公開中の診断セットを1つ解決（非管理者には published のみ返る）。
        const sets = await fetchQuestionSets();
        if (!sets || sets.length === 0) {
          throw new Error('公開中の診断セットが見つかりませんでした。');
        }
        const setId = sets[0].id;
        const data = await fetchQuestionSetById(setId);
        setQuestionSet(data);
        const session = await startSession(setId);
        setSessionId(session.sessionId);
      } catch (err) {
        // 回答実施ガード: 既に回答済み / 有効な回答期間外 は専用メッセージで案内。
        const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        const serverMsg = (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
        if (code === 'ALREADY_ANSWERED') {
          setError(serverMsg || 'この回では既に回答済みです。');
        } else if (code === 'NO_ACTIVE_CAMPAIGN') {
          setError(serverMsg || '現在、回答を受け付けている回答実施がありません（回答期間外です）。');
        } else {
          setError(err instanceof Error ? err.message : '質問セットの取得に失敗しました');
        }
        console.error('Failed to load employee questionnaire:', err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const handleSubmitAnswers = async (answers: UserAnswer[]): Promise<SubmitAnswerResponse> => {
    if (!sessionId) {
      throw new Error('セッションIDが見つかりません');
    }
    // マルチフォーマット対応: scaleValue を持つ回答があれば multi-format を使用。
    const hasScaleAnswers = answers.some((a) => a.scaleValue !== undefined);
    try {
      if (hasScaleAnswers) {
        const multiFormatAnswers = answers.map((answer) => ({
          questionId: answer.questionId,
          answerChoiceId: answer.answerChoiceId || null,
          scaleValue: answer.scaleValue !== undefined ? answer.scaleValue : null,
          departmentId: answer.departmentId || null,
        }));
        await submitMultiFormatAnswersBulk(sessionId, multiFormatAnswers);
      } else {
        const bulkAnswers = answers.map((answer) => ({
          questionId: answer.questionId,
          answerChoiceId: answer.answerChoiceId || '',
        }));
        await submitAnswersBulk(sessionId, bulkAnswers);
      }
    } catch (bulkErr: unknown) {
      const errType = (bulkErr as { response?: { data?: { error?: string } } })?.response?.data
        ?.error;
      if (errType !== 'SESSION_ALREADY_COMPLETED') throw bulkErr;
    }

    try {
      return await completeSession(sessionId);
    } catch (completeErr: unknown) {
      const errType = (completeErr as { response?: { data?: { error?: string } } })?.response?.data
        ?.error;
      if (errType === 'SESSION_ALREADY_COMPLETED') {
        return { sessionId, completedAt: new Date().toISOString(), totalAnswers: answers.length };
      }
      throw completeErr;
    }
  };

  if (!questionSet) {
    return <Centered>{isLoading ? '読み込み中…' : error ?? '読み込み中…'}</Centered>;
  }

  // 完了画面の2ボタンはいずれも社員ダッシュボードへ戻す。
  const backToDashboard = () => navigate('/dashboard', { replace: true });

  return (
    <IntegratedQuestionnaireFlow
      questionSet={questionSet}
      onSubmit={handleSubmitAnswers}
      onNavigateToService={backToDashboard}
      onNavigateToMyPage={backToDashboard}
      isLoading={isLoading}
      error={error}
      designVersion="redesign"
      enableResponsiveLayout={true}
      enableOrientationGuide={false}
      preferredOrientation="portrait"
      forceHorizontalOnMobile={false}
    />
  );
};

export default EmployeeGb74AnswerPage;
